/* eslint-disable prettier/prettier */
/* eslint-disable no-shadow */
import React from 'react';

import { Coordinate } from 'models/shared/types/basic';
import { CircleLayer, HeatmapLayer } from 'mapbox-gl';
import { Marker } from 'react-map-gl';
import Supercluster from 'supercluster';
import { Typography } from 'design_system/src';
import { ReactComponent as Home } from 'design_system/src/static/icons/temple.svg';
import {
	InterestPointType,
	InterestPointTypeValues,
} from 'models/repossession/enums/repossession.enums';

// Este mapa muestra el domicilio declarado de los créditos activos
// (Loan.status = current) y, cuando el vehículo tiene un Tile asignado, su
// última posición reportada.
export interface AddressLocation {
	loanId: string;
	vehicleId: string;
	coordinates: Coordinate;
	createdAt?: string;
	id: string;
	tileId?: string;
	locationType?:
		| InterestPointType.signalTileVehicle
		| InterestPointType.noSignalTileVehicle;
}

const HEATMAP_MAX_ZOOM_LEVEL = 15;

export const hotspotHeatLayerDefinition: HeatmapLayer = {
	id: 'interest_points_heat',
	type: 'heatmap',
	maxzoom: HEATMAP_MAX_ZOOM_LEVEL,
	paint: {
		'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0.01, 1, 1],
		'heatmap-intensity': [
			'interpolate',
			['linear'],
			['zoom'],
			0,
			1,
			HEATMAP_MAX_ZOOM_LEVEL,
			3,
		],
		'heatmap-color': [
			'interpolate',
			['exponential', 30],
			['heatmap-density'],
			0,
			'rgba(111, 157, 236,0)',
			0.5,
			'rgba(111, 157, 236, 0.9)',
			0.8,
			'rgba(236, 237, 239, 0.9)',
			0.85,
			'rgba(255, 216, 171, 0.9)',
			0.9,
			'rgba(223, 32, 64, 0.7)',
			1,
			'rgba(254, 138, 2, 0.9)',
		],
		'heatmap-radius': [
			'interpolate',
			['linear'],
			['zoom'],
			0,
			20,
			HEATMAP_MAX_ZOOM_LEVEL,
			40,
		],
		'heatmap-opacity': [
			'interpolate',
			['linear'],
			['zoom'],
			HEATMAP_MAX_ZOOM_LEVEL - 2,
			1,
			HEATMAP_MAX_ZOOM_LEVEL,
			0,
		],
	},
};

export const hotspotLayerDefinition: CircleLayer = {
	id: 'interest_points_circles',
	type: 'circle',
	minzoom: HEATMAP_MAX_ZOOM_LEVEL - 2,
	paint: {
		'circle-radius': 5,
		'circle-color': 'rgb(0,0,0)',
		'circle-stroke-color': 'white',
		'circle-stroke-width': 1,
		'circle-opacity': [
			'interpolate',
			['linear'],
			['zoom'],
			HEATMAP_MAX_ZOOM_LEVEL - 2,
			0,
			HEATMAP_MAX_ZOOM_LEVEL,
			1,
		],
	},
};

export const createClusterMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	callback: Function,
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;

	return (
		<Marker
			key={`cluster-${cluster.id}`}
			longitude={longitude}
			latitude={latitude}
			onClick={() => callback(cluster)}
		>
			<div className="cluster_1">
				<div className="cluster_2">
					<div className="cluster_3 dim_xxxl">
						<Typography weight="600" scale="xsmall" className="cluster_count">
							{cluster.properties.point_count}
						</Typography>
					</div>
				</div>
			</div>
		</Marker>
	);
};

export const createPointMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	point: AddressLocation,
	callback: Function,
	selected?: boolean,
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;
	const tileType = point.locationType
		? InterestPointTypeValues[point.locationType]
		: null;
	const Icon = tileType?.icon ?? Home;

	return (
		<Marker
			key={point.id}
			longitude={longitude}
			latitude={latitude}
			onClick={() => callback(point)}
		>
			<div
				className={`br_xxl main_marker cursor_pointer ${
					tileType?.color ?? 'primary'
				} ${selected ? 'selected_marker' : ''}`}
			>
				<div className="p_y_xxs p_x_md flex_center z_index_1 pos_relative">
					<Icon className="dim_lg icon" />
					<div className="p_x_md flex_center_col">
						<Typography weight="600" scale="xsmall">
							{point.vehicleId}
						</Typography>
					</div>
				</div>
				<span className="arrow" />
			</div>
		</Marker>
	);
};
